import React, { useRef } from 'react';
import { ClassValue } from 'classnames/types';
import Div from '../Element/Div/Div';
import Span from '../Element/Span/Span';
import BodyText from '../Typography/BodyText/BodyText';
import Gutter from '../Gutter/Gutter';
import Flex from '../Flex/Flex';
import InputMessage from '../Input/InputMessage/InputMessage';
import styles from './TextArea.module.scss';
import { CommonProps } from '../Element/Element';
import { CreateProps } from '../../types/utils/CreateProps';
import 'focus-within-polyfill';

type TextAreaProps = CreateProps<
  {
    /** Label shown on the textArea */
    label: string;
    /** Error text shown below the textArea to indicate the errorneous state */
    errorText?: string;
    /** Additional description for input, if label is not enough */
    helperText?: string;
    /** Sets the textarea in readonly state. Textarea is clickable and focusable but user can't type in it */
    readonly?: boolean;
    /** OnChange handler for the input */
    onChange?: (
      event: React.ChangeEvent<HTMLTextAreaElement>,
      value: string
    ) => void;
  },
  CommonProps<'textarea'>
>;
// Alias for backwards compatibility
export type TextAreaAttributes = TextAreaProps;

const TextArea: React.FunctionComponent<TextAreaProps> = ({
  label,
  value,
  onChange,
  placeholder,
  disabled,
  required,
  errorText,
  helperText,
  readonly,
  id,
  name,
  className,
  ...props
}) => {
  const inputElement = useRef<HTMLTextAreaElement>(null);

  const classNames: ClassValue = [styles.textArea__container];

  if (!!errorText) {
    classNames.push(styles.textArea__container_error);
  }

  if (!!disabled) {
    classNames.push(styles.textArea__container_disabled);
  }

  if (!!className) {
    classNames.push(className);
  }

  const resolveLabel = required ? (
    <>
      <Span>{label} </Span>
      <Span className={styles.textArea_required}>*</Span>
    </>
  ) : (
    label
  );
  const handleInputFocus = () => {
    /* istanbul ignore next */
    if (inputElement.current !== null) {
      inputElement.current.focus();
    }
  };

  return (
    <Div className={styles.textArea}>
      <BodyText
        tagName="span"
        size="sm"
        block
        color={disabled ? 'gray400' : 'black'}
      >
        {resolveLabel}
      </BodyText>
      <Gutter size="xs" />
      <Flex
        occupyVerticalSpace={false}
        className={classNames}
        borderRadius={{ size: 'sm' }}
        padding={{ size: 'sm' }}
        backgroundColor={disabled ? 'gray200' : 'white'}
        onClick={handleInputFocus}
      >
        <textarea
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange?.(e, e.target.value)}
          disabled={disabled}
          ref={inputElement}
          id={id}
          name={name}
          className="inner-textarea-test"
          readOnly={readonly}
          {...props}
        />
      </Flex>

      {!!errorText && errorText !== ' ' && (
        <InputMessage text={errorText} color="red600" />
      )}
      {!!helperText && <InputMessage text={helperText} color="gray500" />}
    </Div>
  );
};

export default TextArea;
